import React, { useEffect, useMemo, useState } from 'react';

import { Flex, HStack } from '@chakra-ui/react';
import styled from '@emotion/styled';
import CustomTagWithoutLabel from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/CustomTagWithoutLabel';
import { useDeleteOrganizationTagMutation } from 'components/RabbitOKRSidePanel/datahooks/useGetCustomTagsOrganization';
import {
  closeAddEditCustomTagsPanel,
  openChangeDetailsPanel,
  openOtherDetailsPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useToast } from 'components/hooks/useToast';
import { useDispatch, useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';
import { themeColors } from 'themes/clients/baseTheme';

import { Plus, Delete, CloseBox, CheckBox, CollapseSidepanel } from 'assets/assetsV1/iconComponents';

import { TOAST_STATUSES } from 'constants/ProjectConstants';

import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextLabel } from 'uiToolkitV1/Typography';

import { SectionHeader } from '../../Components/SectionHeaders';
import { useCustomTagsQuery, useUpdateCustomTagsMutation } from '../../datahooks/useGetCustomTagsOrganization';
import { CustomTagDeletionModal } from './CustomTagDeletionModal';

const ErrorInput = styled(InputV2)`
  border-bottom: 1px solid ${themeColors.alerts.negative};
  &:focus {
    border-bottom: 1px solid ${themeColors.alerts.negative};
  }
`;

export const allowedCharactersRegex = /^[a-zA-Z0-9 _\-]*$/;
export const TAG_MAX_LENGTH = 200;

export default function AddEditCustomTagsPanel() {
  const dispatch = useDispatch();
  const [displayTags, setDisplayTags] = useState([]);
  const [isDeletionModalOpen, setDeletionModalStatus] = useState(false);
  const [toBeDeletedTag, setToBeDeletedTag] = useState(null);
  const [editableTag, setEditableTag] = useState(null);
  const [editTagError, setEditTagError] = useState('');
  const [newTagError, setNewTagError] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [isNewTagInputVisible, setNewTagInputStatus] = useState(false);
  const toast = useToast();

  const createOKRDetails = useSelector(okrCreationPanelStateSelector);
  const editOKRSavedDetails = useSelector(kRDrawerSelector);
  const editOKRDetails = useMemo(
    () => ({ ...editOKRSavedDetails, ...editOKRSavedDetails.localStateValues }),
    [editOKRSavedDetails]
  );

  const {
    details: { okrId, onSave, isOpenAsChild, isTagEditionInSidePanelChildCreation = false },
  } = useSelector(kRDrawerSecondaryPanelSelector);

  const okrDetails = okrId && !isTagEditionInSidePanelChildCreation ? editOKRDetails : createOKRDetails;

  const { tagKeyList = [] } = okrDetails;
  const { error: customTagsError, data: customTagsData } = useCustomTagsQuery();
  const { mutate: deleteOrganizationTag } = useDeleteOrganizationTagMutation((deletedTagId) => {
    if (isOpenAsChild) {
      onSave(tagKeyList.filter((tag) => tag.uuid !== deletedTagId));
    }
  });

  const { mutate: addOrganizationLevelTag } = useUpdateCustomTagsMutation((modifiedOrAddedTag) => {
    if (isOpenAsChild) {
      onSave(
        tagKeyList.map((tag) => {
          if (tag.uuid === modifiedOrAddedTag?.uuid) {
            return { ...modifiedOrAddedTag };
          }
          return tag;
        })
      );
    }
    setEditableTag(null);
    setNewTagName('');
    setNewTagInputStatus(false);
  });

  useEffect(() => {
    if (newTagName?.tagName?.length === TAG_MAX_LENGTH) {
      setNewTagError('Tag name cannot exceed 200 characters.');
    } else if (!allowedCharactersRegex.test(newTagName)) {
      setNewTagError('Special characters are not allowed in the tag name except - and _');
    } else {
      setNewTagError('');
    }
  }, [newTagName]);

  useEffect(() => {
    if (editableTag?.tagName?.length === TAG_MAX_LENGTH) {
      setEditTagError('Tag name cannot exceed 200 characters.');
    } else if (editableTag?.tagName && !allowedCharactersRegex.test(editableTag?.tagName)) {
      setEditTagError('Special characters are not allowed in the tag name except - and _');
    } else {
      setEditTagError('');
    }
  }, [editableTag]);

  useEffect(() => {
    if (customTagsError) {
      toast({
        title: 'Failed to fetch custom tags',
        status: TOAST_STATUSES.ERROR,
      });
      return;
    }

    if (customTagsData) {
      setDisplayTags([...customTagsData]);
    }
  }, [customTagsData, customTagsError]);

  const handTagAddition = () => {
    const newTag = { tagName: newTagName?.trim() };
    if (newTagError) {
      return;
    }
    addOrganizationLevelTag({ tagDetails: newTag });
  };

  const handleTagEdition = () => {
    if (editTagError) {
      return;
    }
    addOrganizationLevelTag({
      tagDetails: { ...editableTag, tagName: editableTag?.tagName?.trim() },
      okrId,
    });
  };

  const handleTagDeletion = (tag) => {
    setDeletionModalStatus(true);
    setToBeDeletedTag(tag);
  };

  const deleteTag = () => {
    setDeletionModalStatus(false);
    deleteOrganizationTag({ tagKeyId: toBeDeletedTag?.uuid, okrId });
  };

  const isEditedTagNameChanged = () => {
    const editedTagIndex = displayTags.findIndex((tag) => tag.uuid === editableTag.uuid);
    if (editedTagIndex !== -1) {
      if (displayTags[editedTagIndex].tagName === editableTag?.tagName?.trim()) {
        return false;
      }
      return true;
    }
    return false;
  };

  const handleCloseAsChild = () => {
    if (okrId && !isTagEditionInSidePanelChildCreation) {
      dispatch(openChangeDetailsPanel({}));
    } else {
      dispatch(
        openOtherDetailsPanel({
          isOpenAsChild: isTagEditionInSidePanelChildCreation,
        })
      );
    }
  };

  return (
    <Flex backgroundColor="#FFFFFF" h="100%" flexDirection="column" justifyContent="space-between">
      <SectionHeader headerText="Edit Tags" h="44px" marginTop="8px">
        <HStack spacing="10px" justify="flex-end">
          <Button
            variant="outlined"
            StartIcon={Plus}
            color="blue"
            onClick={() => setNewTagInputStatus(true)}
            height="32px"
          >
            Add tags
          </Button>
          <Button
            StartIcon={CollapseSidepanel}
            variant="tertiary"
            style={{ height: '32px' }}
            onClick={() => {
              if (isOpenAsChild) {
                handleCloseAsChild();
              } else {
                dispatch(closeAddEditCustomTagsPanel());
              }
            }}
          />
        </HStack>
      </SectionHeader>
      <Divider
        sx={{ borderColor: '#CFD7DF' }}
        marginTop="8px"
        marginBottom="12px"
        marginLeft="auto"
        marginRight="auto"
        width="95%"
      />
      <Flex direction="column" padding="10px" flex={1} height="calc(100% - 133px)" overflow="scroll">
        {displayTags.map((tag) => (
          <Flex>
            {editableTag && tag.uuid === editableTag.uuid ? (
              <Flex direction="column" flex={1}>
                <Flex>
                  <Flex margin="12px" paddingBottom="8px" flex={1}>
                    <ErrorInput
                      placeholder="Enter new tag name"
                      isDashed={false}
                      autoFocus
                      fontSize="14px"
                      value={editableTag.tagName}
                      onChange={(e) => setEditableTag({ ...editableTag, tagName: e.target.value })}
                      borderBottom={`1px solid ${themeColors.alerts.negative}`}
                      maxLength={TAG_MAX_LENGTH}
                      sx={{
                        fontWeight: 'bold !important',
                      }}
                    />
                  </Flex>

                  <Tooltip label="Modify Tag">
                    <Flex
                      justify="center"
                      align="center"
                      mr="5px"
                      onClick={() => {
                        if (isEditedTagNameChanged()) {
                          handleTagEdition();
                        }
                      }}
                    >
                      <CheckBox
                        boxSize="32px"
                        fill="none"
                        color={
                          isEditedTagNameChanged() && editableTag && editableTag?.tagName?.trim()
                            ? themeColors.button.blue.default
                            : themeColors.button.blue.disabled
                        }
                      />
                    </Flex>
                  </Tooltip>
                  <Tooltip label="Cancel">
                    <Flex
                      justify="center"
                      align="center"
                      onClick={() => {
                        setEditableTag(null);
                      }}
                    >
                      <CloseBox boxSize="32px" color={themeColors.button.red.default} fill="none" />
                    </Flex>
                  </Tooltip>
                </Flex>
                <Flex ml="12px" mt="-12px">
                  {editTagError ? (
                    <TextLabel color={themeColors.alerts.negative} bold>
                      {editTagError}
                    </TextLabel>
                  ) : (
                    <TextLabel color={themeColors.alerts.negative} bold>
                      This tag key is already being used. Editing will update elsewhere
                    </TextLabel>
                  )}
                </Flex>
              </Flex>
            ) : (
              <>
                <Flex
                  borderBottom="1px dashed #999999"
                  margin="12px"
                  paddingBottom="8px"
                  width="calc(100% - 54px)"
                  overflow="hidden"
                  onClick={() => setEditableTag(tag)}
                  alignItems="center"
                >
                  <CustomTagWithoutLabel key={tag.uuid} tag={tag} />
                </Flex>
                <Tooltip label="Delete">
                  <Button
                    onClick={() => handleTagDeletion(tag)}
                    variant="rowButtonTertiary"
                    StartIcon={(props) => <Delete {...props} boxSize="24px" color="#666666 !important" />}
                    sx={{
                      paddingRight: '2px !important',
                      height: '40px !important',
                      width: '40px',
                      '&:hover': {
                        svg: {
                          color: '#1565BC !important',
                        },
                      },
                    }}
                  />
                </Tooltip>
              </>
            )}
          </Flex>
        ))}
        {isNewTagInputVisible && (
          <>
            <Flex>
              <Flex margin="12px" paddingBottom="8px" flex={1}>
                {newTagName?.length === TAG_MAX_LENGTH ? (
                  <ErrorInput
                    placeholder="Provide a name to the tag"
                    isDashed
                    autoFocus
                    fontSize="14px"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    maxLength={TAG_MAX_LENGTH}
                    sx={{
                      fontWeight: 'bold !important',
                    }}
                  />
                ) : (
                  <InputV2
                    placeholder="Provide a name to the tag"
                    isDashed
                    autoFocus
                    fontSize="14px"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    maxLength={TAG_MAX_LENGTH}
                    sx={{
                      fontWeight: 'bold !important',
                    }}
                  />
                )}
              </Flex>

              <Tooltip label="Add Tag">
                <Flex
                  justify="center"
                  align="center"
                  mr="5px"
                  onClick={() => {
                    const isEnabled = newTagName && newTagName.trim();
                    if (isEnabled) {
                      handTagAddition();
                    }
                  }}
                >
                  <CheckBox
                    boxSize="32px"
                    fill="none"
                    color={
                      newTagName && newTagName.trim()
                        ? themeColors.button.blue.default
                        : themeColors.button.blue.disabled
                    }
                  />
                </Flex>
              </Tooltip>
              <Tooltip label="Cancel">
                <Flex
                  justify="center"
                  align="center"
                  onClick={() => {
                    setNewTagInputStatus(false);
                    setNewTagName('');
                  }}
                >
                  <CloseBox boxSize="32px" color={themeColors.button.red.default} fill="none" />
                </Flex>
              </Tooltip>
            </Flex>
            {newTagError && (
              <Flex ml="12px" mt="-12px">
                <TextLabel color={themeColors.alerts.negative} bold>
                  {newTagError}
                </TextLabel>
              </Flex>
            )}
          </>
        )}
      </Flex>
      <CustomTagDeletionModal
        isOpen={isDeletionModalOpen}
        onCancel={() => setDeletionModalStatus(false)}
        onContinue={() => deleteTag()}
      />
      <Flex
        flex="auto 0 0"
        borderTopWidth="1px"
        h="72px"
        justifyContent="flex-end"
        alignItems="center"
        backgroundColor={themeColors.neutral.F5F7F9}
        px={3}
      >
        <Button
          variant="filled"
          colorScheme="blue"
          onClick={() => {
            if (isOpenAsChild) {
              handleCloseAsChild();
            } else {
              dispatch(closeAddEditCustomTagsPanel());
            }
          }}
        >
          Close
        </Button>
      </Flex>
    </Flex>
  );
}
